import type { RefObject } from 'react';

import type {
	SelectionExtensionPlugin,
	SelectionExtensionPluginOptions,
} from '@atlaskit/editor-plugin-selection-extension';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider';
import { useEditorToolbarExtensions } from '@confluence/highlight-actions/entry-points/useEditorToolbarExtensions';
import { useJiraWorkCreateSelectionExtension } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtension';
import { useJiraWorkCreateSelectionExtensionV2 } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtensionV2';
import { useNoteSelectionExtension } from '@confluence/note-contextual-create/entry-points/useNoteSelectionExtension';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import { expVal } from '@confluence/feature-experiments';

export const useSelectionExtensionPluginOptions = ({
	isSidePanelOpenRef,
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	contentId,
	editorAPIRef,
}: {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	contentId: string | undefined;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
}): SelectionExtensionPluginOptions => {
	const jiraWorkCreateSelectionExtension = useJiraWorkCreateSelectionExtension({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
	});

	const jiraWorkCreateExtensionV2 = useJiraWorkCreateSelectionExtensionV2({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		editorAPIRef,
	});

	const useConditionalNoteSelectionExtension = conditionalHooksFactory(
		// NEXT PR: switch this to use the feature gate for notes rollout
		() => expVal('cc_notes', 'isEnabled', false),
		useNoteSelectionExtension,
		() => ({ extensionList: [] }),
	);

	// Connect and Forge 2P extensions
	const useExternalExtensionsWithContentId = () => useEditorToolbarExtensions(contentId);
	const useConditionalExternalExtensions = conditionalHooksFactory(
		() => expVal('confluence_enable_2p_toolbar_apps', 'isEnabled', false),
		useExternalExtensionsWithContentId,
		() => [],
	);

	const { extensionList: noteExtensionList } = useConditionalNoteSelectionExtension();
	const externalExtensions = useConditionalExternalExtensions();
	const jiraWorkCreateExtensionList = expVal(
		'confluence-bulk-work-create-editor',
		'isEnabled',
		false,
	)
		? jiraWorkCreateExtensionV2
		: [];

	return {
		extensions: expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
			? {}
			: { firstParty: [jiraWorkCreateSelectionExtension] },
		extensionList: [...noteExtensionList, ...externalExtensions, ...jiraWorkCreateExtensionList],
	};
};
